import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

export type FeedFreshnessResult = "unchanged" | "new_posts" | "failed";
export interface FeedFreshnessTelemetryBatch {
  results: Record<FeedFreshnessResult, number>;
}

export function createFeedFreshnessTelemetryBatcher(input: {
  send: (batch: FeedFreshnessTelemetryBatch) => Promise<void>;
  flushIntervalMs: number;
  maxBatchSize: number;
}) {
  if (
    input.flushIntervalMs < 1 ||
    input.flushIntervalMs > 600_000 ||
    input.maxBatchSize < 1 ||
    input.maxBatchSize > 100
  ) {
    throw new TypeError("Invalid Feed freshness telemetry bounds");
  }
  let results: Record<FeedFreshnessResult, number> = {
    unchanged: 0,
    new_posts: 0,
    failed: 0,
  };
  let timer: ReturnType<typeof setTimeout> | null = null;
  let chain = Promise.resolve();
  let generation = 0;

  const total = () =>
    Object.values(results).reduce((sum, value) => sum + value, 0);
  const schedule = () => {
    if (timer !== null) return;
    const scheduledGeneration = generation;
    timer = setTimeout(() => {
      timer = null;
      if (scheduledGeneration === generation) void flush();
    }, input.flushIntervalMs);
  };
  const take = (): FeedFreshnessTelemetryBatch | null => {
    if (total() === 0) return null;
    let remaining = input.maxBatchSize;
    const batch: FeedFreshnessTelemetryBatch = {
      results: { unchanged: 0, new_posts: 0, failed: 0 },
    };
    for (const result of ["unchanged", "new_posts", "failed"] as const) {
      const count = Math.min(remaining, results[result]);
      batch.results[result] = count;
      results[result] -= count;
      remaining -= count;
    }
    return batch;
  };
  const flush = (): Promise<void> => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const flushGeneration = generation;
    chain = chain.then(async () => {
      while (flushGeneration === generation) {
        const batch = take();
        if (!batch) break;
        try {
          await input.send(batch);
        } catch {
          // Advisory telemetry is deliberately lossy under transport failure.
        }
      }
    });
    return chain;
  };
  return {
    record(result: FeedFreshnessResult): void {
      if (!(["unchanged", "new_posts", "failed"] as const).includes(result)) {
        throw new TypeError("Invalid Feed freshness result");
      }
      results[result] += 1;
      schedule();
      if (total() >= input.maxBatchSize) void flush();
    },
    flush,
    reset(): void {
      generation += 1;
      results = { unchanged: 0, new_posts: 0, failed: 0 };
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}

interface FreshnessSnapshot<Page> {
  head: Page | null;
  error: boolean;
  checking: boolean;
  applying: boolean;
}

interface FreshnessEntry<Page> {
  queryClient: QueryClient;
  queryKey: QueryKey;
  registryKey: string;
  intervalMs: number;
  loadHead: (signal: AbortSignal) => Promise<Page>;
  subscribers: Set<(snapshot: FreshnessSnapshot<Page>) => void>;
  snapshot: FreshnessSnapshot<Page>;
  generation: number;
  interval: ReturnType<typeof setInterval> | null;
  applyController: AbortController | null;
  applyPromise: Promise<void> | null;
  check: () => Promise<void>;
  pause: () => void;
  apply: (
    work: (head: Page, signal: AbortSignal) => Promise<boolean>,
  ) => Promise<void>;
  clearHead: () => void;
  onFocus: () => void;
  onOnline: () => void;
  onOffline: () => void;
  onVisibility: () => void;
}

const entries = new WeakMap<
  QueryClient,
  Map<string, FreshnessEntry<unknown>>
>();

function idle<Page>(): FreshnessSnapshot<Page> {
  return { head: null, error: false, checking: false, applying: false };
}

function browserActive(): boolean {
  return (
    (typeof document === "undefined" ||
      document.visibilityState === "visible") &&
    (typeof navigator === "undefined" || navigator.onLine !== false)
  );
}

function publish<Page>(
  entry: FreshnessEntry<Page>,
  patch: Partial<FreshnessSnapshot<Page>>,
): void {
  entry.snapshot = { ...entry.snapshot, ...patch };
  for (const subscriber of entry.subscribers) subscriber(entry.snapshot);
}

function cancelRead<Page>(entry: FreshnessEntry<Page>): void {
  entry.generation += 1;
  void entry.queryClient.cancelQueries({
    queryKey: entry.queryKey,
    exact: true,
  });
  publish(entry, { checking: false });
}

function createEntry<Page>(input: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  registryKey: string;
  intervalMs: number;
  loadHead: (signal: AbortSignal) => Promise<Page>;
}): FreshnessEntry<Page> {
  const entry: FreshnessEntry<Page> = {
    ...input,
    subscribers: new Set<(snapshot: FreshnessSnapshot<Page>) => void>(),
    snapshot: idle<Page>(),
    generation: 0,
    interval: null,
    applyController: null,
    applyPromise: null,
    check: async () => undefined,
    pause: () => undefined,
    apply: async (_work) => undefined,
    clearHead: () => {},
    onFocus: () => undefined,
    onOnline: () => undefined,
    onOffline: () => undefined,
    onVisibility: () => undefined,
  };

  entry.check = async () => {
    if (
      entry.subscribers.size === 0 ||
      entry.snapshot.checking ||
      entry.snapshot.applying ||
      !browserActive()
    ) {
      return;
    }
    const generation = ++entry.generation;
    publish(entry, { checking: true });
    try {
      const head = await entry.queryClient.fetchQuery({
        queryKey: entry.queryKey,
        queryFn: ({ signal }) => entry.loadHead(signal),
        staleTime: 0,
        gcTime: entry.intervalMs,
        retry: false,
      });
      if (generation !== entry.generation || !browserActive()) return;
      publish(entry, { head, error: false });
    } catch {
      if (generation === entry.generation && browserActive()) {
        publish(entry, { error: true });
      }
    } finally {
      if (generation === entry.generation) publish(entry, { checking: false });
    }
  };
  entry.pause = () => {
    cancelRead(entry);
    entry.applyController?.abort();
    entry.applyController = null;
    publish(entry, { applying: false });
  };
  entry.apply = async (work) => {
    if (entry.applyPromise) return entry.applyPromise;
    const head = entry.snapshot.head;
    if (!head || entry.subscribers.size === 0) return;
    if (entry.snapshot.checking) return;
    const generation = entry.generation;
    const controller = new AbortController();
    entry.applyController = controller;
    publish(entry, { applying: true });
    let superseded = false;
    const promise = (async () => {
      try {
        const installed = await work(head, controller.signal);
        if (generation === entry.generation && !controller.signal.aborted) {
          if (installed) publish(entry, { head: null, error: false });
          else superseded = true;
        }
      } catch {
        if (generation === entry.generation && !controller.signal.aborted) {
          publish(entry, { error: true });
        }
      } finally {
        if (entry.applyController === controller) {
          entry.applyController = null;
        }
        entry.applyPromise = null;
        if (generation === entry.generation)
          publish(entry, { applying: false });
        if (superseded && generation === entry.generation) {
          void entry.check();
        }
      }
    })();
    entry.applyPromise = promise;
    return promise;
  };
  entry.clearHead = () => publish(entry, { head: null, error: false });
  entry.onFocus = () => void entry.check();
  entry.onOnline = () => void entry.check();
  entry.onOffline = () => entry.pause();
  entry.onVisibility = () => {
    if (document.visibilityState === "visible") void entry.check();
    else entry.pause();
  };
  return entry;
}

function start<Page>(entry: FreshnessEntry<Page>): void {
  entry.interval = window.setInterval(
    () => void entry.check(),
    entry.intervalMs,
  );
  window.addEventListener("focus", entry.onFocus);
  window.addEventListener("online", entry.onOnline);
  window.addEventListener("offline", entry.onOffline);
  document.addEventListener("visibilitychange", entry.onVisibility);
}

function stop<Page>(entry: FreshnessEntry<Page>): void {
  if (entry.interval !== null) window.clearInterval(entry.interval);
  entry.interval = null;
  window.removeEventListener("focus", entry.onFocus);
  window.removeEventListener("online", entry.onOnline);
  window.removeEventListener("offline", entry.onOffline);
  document.removeEventListener("visibilitychange", entry.onVisibility);
  entry.pause();
  entry.queryClient.removeQueries({ queryKey: entry.queryKey, exact: true });
  const clientEntries = entries.get(entry.queryClient);
  clientEntries?.delete(entry.registryKey);
  if (clientEntries?.size === 0) entries.delete(entry.queryClient);
}

function getEntry<Page>(input: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  registryKey: string;
  intervalMs: number;
  loadHead: (signal: AbortSignal) => Promise<Page>;
}): FreshnessEntry<Page> {
  const clientEntries = entries.get(input.queryClient) ?? new Map();
  entries.set(input.queryClient, clientEntries);
  const existing = clientEntries.get(input.registryKey) as
    | FreshnessEntry<Page>
    | undefined;
  if (existing) {
    existing.loadHead = input.loadHead;
    return existing;
  }
  const created = createEntry(input);
  clientEntries.set(input.registryKey, created as FreshnessEntry<unknown>);
  return created;
}

export function useBoundedFeedFreshness<Page>(input: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  registryKey: string;
  enabled: boolean;
  intervalMs: number;
  loadHead: (signal: AbortSignal) => Promise<Page>;
}) {
  const entry = useMemo(
    () =>
      input.enabled
        ? getEntry({
            queryClient: input.queryClient,
            queryKey: input.queryKey,
            registryKey: input.registryKey,
            intervalMs: input.intervalMs,
            loadHead: input.loadHead,
          })
        : null,
    [
      input.enabled,
      input.intervalMs,
      input.queryClient,
      input.queryKey,
      input.registryKey,
    ],
  );
  if (entry) entry.loadHead = input.loadHead;
  const [observed, setObserved] = useState<{
    entry: FreshnessEntry<Page> | null;
    snapshot: FreshnessSnapshot<Page>;
  }>(() => ({ entry, snapshot: entry?.snapshot ?? idle<Page>() }));

  useEffect(() => {
    if (!entry) {
      setObserved({ entry: null, snapshot: idle<Page>() });
      return;
    }
    const subscriber = (next: FreshnessSnapshot<Page>) =>
      setObserved({ entry, snapshot: next });
    entry.subscribers.add(subscriber);
    subscriber(entry.snapshot);
    if (entry.subscribers.size === 1) start(entry);
    return () => {
      entry.subscribers.delete(subscriber);
      if (entry.subscribers.size === 0) stop(entry);
    };
  }, [entry]);

  const check = useCallback(() => entry?.check() ?? Promise.resolve(), [entry]);
  const apply = useCallback(
    (work: (head: Page, signal: AbortSignal) => Promise<boolean>) =>
      entry?.apply(work) ?? Promise.resolve(),
    [entry],
  );
  const clearHead = useCallback(() => entry?.clearHead(), [entry]);
  const effective =
    observed.entry === entry
      ? observed.snapshot
      : (entry?.snapshot ?? idle<Page>());
  return { ...effective, check, apply, clearHead };
}
